import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Tag, Package, CheckCircle, Send, Trash2, Search, Filter, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import StaffIdInput from '@/components/StaffIdInput';
import { validateBundleFormat } from '@/utils/validation';

// Searchable Dropdown Component
const SearchableDropdown = ({ 
  label, 
  placeholder, 
  options, 
  value, 
  onChange, 
  disabled, 
  isLoading,
  displayKey = null,
  valueKey = null,
  testId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(opt => {
      const displayValue = displayKey ? opt[displayKey] : opt;
      return displayValue?.toLowerCase().includes(term);
    });
  }, [options, searchTerm, displayKey]);

  const getDisplayValue = (opt) => displayKey ? opt[displayKey] : opt;
  const getOptionValue = (opt) => valueKey ? opt[valueKey] : opt;

  const selectedDisplay = useMemo(() => {
    if (!value) return '';
    const found = options.find(opt => getOptionValue(opt) === value);
    return found ? getDisplayValue(found) : value;
  }, [value, options, displayKey, valueKey]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative" ref={dropdownRef}>
        <div
          className={`flex items-center h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed bg-gray-100' : 'hover:border-gray-400'}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          data-testid={testId}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : (
            <span className={selectedDisplay ? 'text-foreground' : 'text-muted-foreground'}>
              {selectedDisplay || placeholder}
            </span>
          )}
        </div>
        
        {isOpen && !disabled && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden">
            {/* Search Input */}
            <div className="p-2 border-b sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="pl-8 h-9"
                  autoFocus
                />
              </div>
            </div>
            
            {/* Options List */}
            <div className="max-h-48 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No results found</div>
              ) : (
                filteredOptions.map((opt, idx) => (
                  <div
                    key={idx}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 ${getOptionValue(opt) === value ? 'bg-blue-50 text-blue-700' : ''}`}
                    onClick={() => {
                      onChange(getOptionValue(opt));
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                  >
                    {getDisplayValue(opt)}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const FGAssignment = ({
  title = 'Operation 2: FG Assignment',
  subtitle = 'Assign Finished Goods to bundles',
  backRoute = '/sections/goods-transfer',
  apiBase = '/goods-transfer/fg-assignment',
  checkBunFormat = true,
  onAssigned,
  children,
}) => {
  const navigate = useNavigate();
  const [staffId, setStaffId] = useState('');
  const [staffName, setStaffName] = useState('');
  const [staffIdLocked, setStaffIdLocked] = useState(false);
  
  // Category4 Filter (loads first for performance)
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  
  // FG Selection with loading states
  const [designs, setDesigns] = useState([]);
  const [shades, setShades] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [items, setItems] = useState([]);
  
  const [isDesignsLoading, setIsDesignsLoading] = useState(false);
  const [isShadesLoading, setIsShadesLoading] = useState(false);
  const [isSizesLoading, setIsSizesLoading] = useState(false);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  
  const [selectedDesign, setSelectedDesign] = useState('');
  const [selectedShade, setSelectedShade] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Bundle scanning
  const [bundleBarcode, setBundleBarcode] = useState('');
  const [scannedBundles, setScannedBundles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const staffInputRef = useRef(null);
  const bundleInputRef = useRef(null);

  // Fetch categories on mount (for filtering)
  useEffect(() => {
    fetchCategories();
  }, []);

  // Fetch designs when category changes
  useEffect(() => {
    if (selectedCategory) {
      fetchDesigns(selectedCategory);
      // Reset downstream selections
      setSelectedDesign('');
      setSelectedShade('');
      setSelectedSize('');
      setSelectedItem(null);
      setShades([]);
      setSizes([]);
      setItems([]);
    }
  }, [selectedCategory]);

  // Fetch shades when design changes
  useEffect(() => {
    if (selectedDesign) {
      fetchShades(selectedDesign);
      setSelectedShade('');
      setSelectedSize('');
      setSelectedItem(null);
      setSizes([]);
      setItems([]);
    }
  }, [selectedDesign]);

  // Fetch sizes when shade changes
  useEffect(() => {
    if (selectedDesign && selectedShade) {
      fetchSizes(selectedDesign, selectedShade);
      setSelectedSize('');
      setSelectedItem(null);
      setItems([]);
    }
  }, [selectedShade]);

  // Lookup items when size changes
  useEffect(() => {
    if (selectedDesign && selectedShade && selectedSize) {
      lookupItems(selectedDesign, selectedShade, selectedSize);
      setSelectedItem(null);
    }
  }, [selectedSize]);

  const fetchCategories = async () => {
    setIsCategoriesLoading(true);
    try {
      const response = await apiClient.get('/goods-transfer/item-master/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const fetchDesigns = async (category4) => {
    setIsDesignsLoading(true);
    try {
      const params = new URLSearchParams();
      if (category4) params.append('category4', category4);
      const response = await apiClient.get(`/goods-transfer/item-master/designs?${params}`);
      setDesigns(response.data.designs || []);
    } catch (error) {
      console.error('Error fetching designs:', error);
      toast.error('Failed to load designs');
    } finally {
      setIsDesignsLoading(false);
    }
  };

  const fetchShades = async (designName) => {
    setIsShadesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('design_name', designName);
      if (selectedCategory) params.append('category4', selectedCategory);
      const response = await apiClient.get(`/goods-transfer/item-master/shades?${params}`);
      const fetchedShades = response.data.shades || [];
      setShades(fetchedShades);
      
      // Auto-select if only one shade available
      if (fetchedShades.length === 1) {
        setSelectedShade(fetchedShades[0]);
        toast.info(`Auto-selected shade: ${fetchedShades[0]}`);
      }
    } catch (error) {
      console.error('Error fetching shades:', error);
    } finally {
      setIsShadesLoading(false);
    }
  };

  const fetchSizes = async (designName, shade) => {
    setIsSizesLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('design_name', designName);
      params.append('shade', shade);
      if (selectedCategory) params.append('category4', selectedCategory);
      const response = await apiClient.get(`/goods-transfer/item-master/sizes?${params}`);
      const fetchedSizes = response.data.sizes || [];
      setSizes(fetchedSizes);
      
      // Auto-select if only one size available
      if (fetchedSizes.length === 1) {
        setSelectedSize(fetchedSizes[0]);
        toast.info(`Auto-selected size: ${fetchedSizes[0]}`);
      }
    } catch (error) {
      console.error('Error fetching sizes:', error);
    } finally {
      setIsSizesLoading(false);
    }
  };

  const lookupItems = async (designName, shade, size) => {
    setIsItemsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('design_name', designName);
      params.append('shade', shade);
      params.append('size', size);
      if (selectedCategory) params.append('category4', selectedCategory);
      const response = await apiClient.get(`/goods-transfer/item-master/lookup?${params}`);
      const fetchedItems = response.data.items || [];
      setItems(fetchedItems);
      
      // Auto-select if only one item available
      if (fetchedItems.length === 1) {
        setSelectedItem(fetchedItems[0]);
        toast.info(`Auto-selected item: ${fetchedItems[0].barcode}`);
      }
    } catch (error) {
      console.error('Error looking up items:', error);
    } finally {
      setIsItemsLoading(false);
    }
  };

  const handleStaffValidated = (result) => {
    if (result.valid) {
      setStaffId(result.staffId);
      setStaffName(result.staffName || '');
      setStaffIdLocked(true);
    }
  };

  const handleUnlockStaff = () => {
    setStaffIdLocked(false);
    setStaffId('');
    setStaffName('');
  };

  const handleBundleScan = async (e) => {
    e.preventDefault();
    
    const code = bundleBarcode.trim().toUpperCase();
    if (!code) {
      toast.error('Please scan bundle barcode');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    // Validate bundle format - must start with BUN and be 15 characters
    const formatValidation = checkBunFormat ? validateBundleFormat(code) : { valid: true };
    if (!formatValidation.valid) {
      toast.error(formatValidation.error);
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    // Check if already scanned
    if (scannedBundles.find(b => b.barcode === code)) {
      toast.error('Bundle already scanned');
      setBundleBarcode('');
      bundleInputRef.current?.focus();
      return;
    }

    setIsProcessing(true);
    try {
      const response = await apiClient.post(`${apiBase}/validate-bundle?bundle_barcode=${encodeURIComponent(code)}`);
      
      if (response.data.valid) {
        setScannedBundles(prev => [{
          barcode: response.data.bundle_barcode || code,
          source: response.data.source,
          usn_count: response.data.usn_count,
          flow_type: response.data.flow_type
        }, ...prev]);
        toast.success(`Bundle added! Total: ${scannedBundles.length + 1}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Invalid bundle';
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
      setBundleBarcode('');
      // Use setTimeout to ensure focus after state updates
      setTimeout(() => bundleInputRef.current?.focus(), 50);
    }
  };

  const handleRemoveBundle = (barcode) => {
    setScannedBundles(prev => prev.filter(b => b.barcode !== barcode));
    toast.success('Bundle removed');
  };

  const handleSubmit = async () => {
    if (!selectedItem) {
      toast.error('Please select an item');
      return;
    }

    if (scannedBundles.length === 0) {
      toast.error('No bundles scanned');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await apiClient.post(`${apiBase}/submit`, {
        staff_id: staffId,
        item_barcode: selectedItem.barcode,
        design_name: selectedItem.design_name,
        shade: selectedItem.shade,
        size: selectedItem.size,
        description: selectedItem.description,
        bundle_barcodes: scannedBundles.map(b => b.barcode)
      });
      
      toast.success(response.data.message);
      (response.data.warnings || []).forEach((w) => toast.warning(w));
      onAssigned?.();
      
      // Reset for next assignment
      setScannedBundles([]);
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    } catch (error) {
      console.error('Submit error:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail.map(e => e.msg || e).join(', ') : 'Failed to submit');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFGSelected = selectedItem !== null;

  // Item dropdown display formatter
  const itemOptions = items.map(item => ({
    ...item,
    displayLabel: `${item.description} (${item.barcode})`
  }));

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(backRoute)} className="text-[#F9FAFB] hover:bg-white/10" data-testid="fg-assignment-back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display" data-testid="fg-assignment-title">{title}</h1>
              <p className="text-sm text-[#9CA3AF]">{subtitle}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4">
        {/* Step 1: Staff ID */}
        <Card className="bg-white border-2 border-orange-200 shadow-md">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <span className="w-7 h-7 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <User className="w-5 h-5 text-orange-500" />
              Staff Identification
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!staffIdLocked ? (
              <StaffIdInput
                value={staffId}
                onChange={setStaffId}
                onValidated={handleStaffValidated}
                placeholder="Scan or enter Staff ID..."
                autoFocus
                showAutocomplete={true}
                label=""
              />
            ) : (
              <div className="flex items-center justify-between">
                <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-lg">
                  <span className="font-mono font-semibold">{staffId}</span>
                  {staffName && <span className="ml-2 text-orange-600">- {staffName}</span>}
                </div>
                <Button variant="outline" size="sm" onClick={handleUnlockStaff}>
                  Change
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Step 2: Select FG with Category Filter */}
        {staffIdLocked && (
          <Card className="bg-white border-2 border-green-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                <Tag className="w-5 h-5 text-green-500" />
                Select Finished Goods
              </CardTitle>
              <CardDescription>
                <span className="flex items-center gap-1">
                  <Filter className="w-3 h-3" />
                  Filter by Category4 first, then select Design → Shade → Size → Item
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Category4 Filter - First selection for performance */}
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <SearchableDropdown
                  label="Category 4 (Filter First)"
                  placeholder="Select category to filter items..."
                  options={categories}
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  disabled={false}
                  isLoading={isCategoriesLoading}
                  testId="category4-dropdown"
                />
                {selectedCategory && (
                  <p className="mt-2 text-xs text-yellow-700">
                    Filtering by: <strong>{selectedCategory}</strong>
                    <button 
                      onClick={() => {
                        setSelectedCategory('');
                        setSelectedDesign('');
                        setSelectedShade('');
                        setSelectedSize('');
                        setSelectedItem(null);
                        setDesigns([]);
                        setShades([]);
                        setSizes([]);
                        setItems([]);
                      }}
                      className="ml-2 text-red-600 hover:underline"
                    >
                      Clear
                    </button>
                  </p>
                )}
              </div>

              {/* Cascading dropdowns - only enabled after category selection */}
              <div className="grid grid-cols-2 gap-4">
                <SearchableDropdown
                  label="Design Name"
                  placeholder={selectedCategory ? "Search & select design..." : "Select category first"}
                  options={designs}
                  value={selectedDesign}
                  onChange={setSelectedDesign}
                  disabled={!selectedCategory}
                  isLoading={isDesignsLoading}
                  testId="design-dropdown"
                />

                <SearchableDropdown
                  label="Shade"
                  placeholder={selectedDesign ? "Search & select shade..." : "Select design first"}
                  options={shades}
                  value={selectedShade}
                  onChange={setSelectedShade}
                  disabled={!selectedDesign}
                  isLoading={isShadesLoading}
                  testId="shade-dropdown"
                />

                <SearchableDropdown
                  label="Size"
                  placeholder={selectedShade ? "Search & select size..." : "Select shade first"}
                  options={sizes}
                  value={selectedSize}
                  onChange={setSelectedSize}
                  disabled={!selectedShade}
                  isLoading={isSizesLoading}
                  testId="size-dropdown"
                />

                <SearchableDropdown
                  label="Description / Barcode"
                  placeholder={selectedSize ? "Search & select item..." : "Select size first"}
                  options={itemOptions}
                  value={selectedItem?.barcode || ''}
                  onChange={(barcode) => setSelectedItem(items.find(i => i.barcode === barcode))}
                  disabled={!selectedSize || items.length === 0}
                  isLoading={isItemsLoading}
                  displayKey="displayLabel"
                  valueKey="barcode"
                  testId="item-dropdown"
                />
              </div>

              {selectedItem && (
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm font-semibold text-green-800">Selected Item:</p>
                  <p className="font-mono text-lg">{selectedItem.barcode}</p>
                  <p className="text-sm text-gray-600">
                    {selectedItem.design_name} | {selectedItem.shade} | {selectedItem.size}
                  </p>
                  <p className="text-sm text-gray-500">{selectedItem.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Scan Bundles */}
        {isFGSelected && (
          <Card className="bg-white border-2 border-blue-200 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <span className="w-7 h-7 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                <Package className="w-5 h-5 text-blue-500" />
                Scan Bundles
              </CardTitle>
              <CardDescription>Scan bundle barcodes to assign the selected FG</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBundleScan} className="flex gap-2">
                <Input
                  ref={bundleInputRef}
                  value={bundleBarcode}
                  onChange={(e) => setBundleBarcode(e.target.value)}
                  placeholder="Scan Bundle Barcode..."
                  className="text-lg h-12 font-mono"
                  disabled={isProcessing}
                  autoComplete="off"
                  data-testid="bundle-barcode-input"
                />
                <Button type="submit" disabled={isProcessing} className="bg-blue-500 hover:bg-blue-600 h-12 px-6" data-testid="add-bundle-btn">
                  {isProcessing ? 'Validating...' : 'Add'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Scanned Bundles List */}
        {scannedBundles.length > 0 && (
          <Card className="bg-white border-none shadow-md">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  Scanned Bundles ({scannedBundles.length})
                </CardTitle>
                <Button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="assign-fg-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {isSubmitting ? 'Assigning...' : 'Assign FG'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {scannedBundles.map((bundle, index) => (
                  <div key={bundle.barcode} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid={`scanned-bundle-${bundle.barcode}`}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500 w-8">#{scannedBundles.length - index}</span>
                      <div>
                        <span className="font-mono font-semibold">{bundle.barcode}</span>
                        {bundle.usn_count > 0 && (
                          <span className="ml-2 text-xs text-gray-500">({bundle.usn_count} USNs)</span>
                        )}
                        {bundle.flow_type && (
                          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">{bundle.flow_type}</span>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleRemoveBundle(bundle.barcode)}
                      className="text-red-500 hover:bg-red-100"
                      data-testid={`remove-bundle-${bundle.barcode}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {children}
      </main>
    </div>
  );
};

export default FGAssignment;
